import { faExclamationTriangle, faSpinner } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { CSSProperties, useState } from 'react'
import { ArcSeries, RadialChart, RadialChartPoint, RadialChartProps } from 'react-vis'
import { CurrencyCode } from '@citruscamps/citrus-client'
import { toCurrency } from '../../utils/currency'

interface ITickLabelProps extends Omit<IProps, 'data'> {
  data: RadialChartProps['data'] | undefined
  amount?: number
  total?: number
  selectedPoint?: RadialChartPoint
}

export const DefaultTickLabel = (props: ITickLabelProps) => {
  const {
    unit = 'per',
    currency,
    language,
    loading = false,
    hideTotal = false,
    data,
    amount,
    total,
    selectedPoint,
  } = props
  return (
    <>
      <div className="text-primary">
        {typeof total === 'undefined' || data?.length === 0 || loading
          ? '\u00A0'
          : (amount
              ? toUnit(amount, unit, currency, language)
              : total && !hideTotal
              ? toUnit(total, unit, currency, language)
              : 0) || '\u00A0'}
      </div>{' '}
      <div className="small text-body-secondary">{selectedPoint?.label || '\u00A0'}</div>
    </>
  )
}

const toUnit = (
  amount: number,
  unit: 'per' | 'currency' | 'percent',
  currency?: CurrencyCode,
  language?: string,
): string => {
  switch (unit) {
    case 'per':
      return `${amount}`
    case 'currency':
      return currency && language ? toCurrency({ amount, currency, language }).display : `${amount}`
    case 'percent':
      return `${amount}%`
    default:
      return ''
  }
}

export interface CitrusRadialChartPoint extends RadialChartPoint {
  id: string | null
}

interface IProps {
  title?: string
  unit?: 'per' | 'currency' | 'percent'
  summaryType?: 'sum' | 'average'
  currency?: CurrencyCode
  language?: string
  data?: CitrusRadialChartPoint[]
  loading?: boolean
  hideTotal?: boolean
  summaryLabel?: () => string
  onValueClick?: (params: any) => any
  TickLabel?: React.FC<ITickLabelProps>
}

export const RadialPlot = (props: IProps) => {
  const { title, loading = false, TickLabel = DefaultTickLabel, onValueClick } = props
  const [selectedPoint, setSelectedPoint] = useState<RadialChartPoint | undefined>()
  let total: number = 0
  const data: RadialChartProps['data'] | undefined =
    props.data &&
    props.data
      .filter((d) => d.angle !== 0)
      .map(
        (
          d: CitrusRadialChartPoint,
          i: number,
          arr: CitrusRadialChartPoint[],
        ): {
          angle?: number | undefined
          className?: string | undefined
          label?: string | undefined
          radius?: number | undefined
          style?: CSSProperties | undefined
        } => {
          total += d.angle || 0
          return {
            ...d,
            style: {
              opacity: props.data ? (i + 1) / arr.length : 1,
            },
          }
        },
      )
  const amount: number | undefined = props.data?.find(
    (d) => d.id === selectedPoint?.subLabel,
  )?.amount

  return (
    <div className="position-relative p-2 mb-lg-5">
      <div className="position-absolute start-0 m-2">
        <div className="small">{title || '\u00A0'}</div>
        <TickLabel
          {...props}
          data={data}
          amount={amount}
          total={total}
          selectedPoint={selectedPoint}
        />
      </div>
      {loading ? (
        <div
          className="text-body-secondary pb-5 text-center"
          style={{ paddingTop: '5rem', minHeight: 164 }}
        >
          <FontAwesomeIcon icon={faSpinner} className="me-2" pulse />
          Loading
        </div>
      ) : typeof data !== 'undefined' && props.data!.length > 0 ? (
        total === 0 ? (
          <div
            className="text-body-secondary pb-5 text-center"
            style={{ paddingTop: '5rem', minHeight: 164 }}
          >
            There are no results at this time.
          </div>
        ) : (
          <div
            className="d-flex "
            style={{ paddingTop: '4.25rem', transform: 'translate(25px)' }}
            onMouseLeave={() => setSelectedPoint(undefined)}
          >
            <RadialChart
              data={data}
              width={100}
              height={100}
              className="mx-auto mt-lg-3"
              style={onValueClick && { cursor: 'pointer' }}
              colorRange={[getComputedStyle(document.body).getPropertyValue('--bs-primary')]}
              padAngle={0.04}
              innerRadius={40}
              radius={50}
              onValueMouseOver={(point) => {
                setSelectedPoint(point)
              }}
              onValueClick={(value) => {
                onValueClick && onValueClick(value)
              }}
            >
              <ArcSeries
                color={getComputedStyle(document.body).getPropertyValue('--bs-primary')}
                animation
              />
            </RadialChart>
          </div>
        )
      ) : (
        <div
          className="text-body-secondary pb-5 text-center"
          style={{ paddingTop: '5rem', minHeight: 164 }}
        >
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          Failed to load analytics.
        </div>
      )}
    </div>
  )
}
